import React, { useState, useRef, ChangeEvent, CSSProperties, useEffect } from "react";

import classNames from "classnames";

import * as styles from "./AutoResizableTextArea.css";

interface AutoResizableTextAreaProps {
  style?: CSSProperties;
  placeholder?: string;
  onTextChange?: (text: string) => void;
  defaultValue?: string;
  overrideText?: string;
  handleSubmit?: () => void;
  shouldSubmitOnEnter?: boolean;
  resizeLimit?: number;
  shouldFocusTextArea?: boolean;
  characterLimit?: number; // Added character limit prop
  className?: string;
}

export const AutoResizableTextArea: React.FC<AutoResizableTextAreaProps> = ({
  style,
  placeholder = "",
  onTextChange,
  defaultValue = "",
  overrideText = "",
  handleSubmit,
  shouldSubmitOnEnter = true,
  resizeLimit = undefined,
  shouldFocusTextArea = false,
  characterLimit, // Added character limit to the destructured props
  className,
}) => {
  const [text, setText] = useState<string>(defaultValue);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const newText = event.target.value;
    // Check if the new text exceeds the character limit, if a limit is set
    if (!characterLimit || newText.length <= characterLimit) {
      setText(newText);
      autoResize(event.target);
      onTextChange && onTextChange(newText);
    }
  };

  useEffect(() => {
    if (overrideText !== text && (!characterLimit || overrideText.length <= characterLimit)) {
      setText(overrideText);
    }
  }, [overrideText, characterLimit]);

  // Focus the textarea when the component mounts
  useEffect(() => {
    if (textareaRef.current && shouldFocusTextArea) {
      textareaRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 700);
    }
  }, []); // Empty dependency array means this effect runs once on mount

  useEffect(() => {
    if (shouldSubmitOnEnter) {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Enter" && handleSubmit) {
          if (!event.shiftKey) {
            event.preventDefault();
            handleSubmit();
          }
        }
      };

      document.addEventListener("keydown", handleKeyDown);

      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [handleSubmit, shouldSubmitOnEnter]);

  const autoResize = (element: HTMLTextAreaElement) => {
    element.style.height = "auto";
    if (resizeLimit && resizeLimit > 0) {
      const sizeForHeight = element.scrollHeight > resizeLimit ? resizeLimit : element.scrollHeight;
      element.style.height = `${sizeForHeight}px`;
    } else {
      element.style.height = `${element.scrollHeight}px`;
    }
  };

  // Determine if the text exceeds the character limit
  const isOverLimit = characterLimit !== undefined && text.length >= characterLimit;

  // Dynamically update the textarea style to include a red border if over the character limit
  const textareaStyle = {
    ...style,
    border: isOverLimit ? "1px solid #FF6161" : style?.border,
  };

  return (
    <textarea
      ref={textareaRef}
      value={text}
      onChange={handleChange}
      placeholder={placeholder}
      // style={textareaStyle}
      className={classNames(styles.promptTextArea, className)}
      maxLength={characterLimit} // Use the maxLength attribute to enforce the character limit
    />
  );
};

export default AutoResizableTextArea;
